/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import msdyn365Commerce, { getLargestGridSettingViewportName, IGeneric, isAuthoringEditEnabled } from '@msdyn365-commerce/core-internal';
import * as React from 'react';
import { isEqual as _isEqual } from 'lodash';
import {
    buildBackgroundImageStylesByViewport,
    getBackgroundImageAttributes,
    getBackgroundImageLazyloadAttributes
} from './container-background-image-helper';
import {
    IBackgroundImageAttributes,
    IBackgroundImageProps,
    IBackgroundImageState,
    IBackgroundImageStyle
} from './container-background-image-interfaces';

const BACKGROUND_IMAGE_CLASS_NAME = 'ms-container-background-image';

/**
 * BackgroundImage component
 * @extends {React.PureComponent<IBackgroundImageProps, IBackgroundImageState>}
 */
export class BackgroundImage extends React.PureComponent<IBackgroundImageProps, IBackgroundImageState> {
    private stylesByViewport: IGeneric<IBackgroundImageStyle>;
    private currentStyle: IBackgroundImageStyle | undefined;
    private mounted: boolean;

    constructor(props: IBackgroundImageProps, state: IBackgroundImageState) {
        super(props);
        this.stylesByViewport = buildBackgroundImageStylesByViewport(this.props);
        this._onChangeHandler = this._onChangeHandler.bind(this);
        this.currentStyle = this._getCurrentStyle();
        this.mounted = false;
        this._componentResize = this._componentResize.bind(this);
        this._getCurrentStyle = this._getCurrentStyle.bind(this);
        this.state = { updateAttributes: false };
    }

    public componentDidMount(): void {
        if (msdyn365Commerce.isBrowser) {
            window.addEventListener('resize', this._componentResize);
            this.mounted = true;
            this._componentResize();
        }
    }

    public componentWillUnmount(): void {
        if (msdyn365Commerce.isBrowser) {
            window.removeEventListener('resize', this._componentResize);
            this.mounted = false;
        }
    }

    public shouldComponentUpdate(nextProps: IBackgroundImageProps, nextState: { updateAttributes: boolean }): boolean {
        if (
            this.state !== nextState ||
            this.props.request?.gridSettings !== nextProps.request?.gridSettings ||
            this.props.configs.repeat !== nextProps.configs.repeat ||
            this.props.configs.size !== nextProps.configs.size ||
            this.props.configs.layout !== nextProps.configs.layout ||
            this.props.image?.imageSettings !== nextProps.image?.imageSettings ||
            this.props.image?.src !== nextProps.image?.src
        ) {
            return true;
        }
        return false;
    }

    public render(): JSX.Element | null {
        const { children } = this.props;

        try {
            this.stylesByViewport = buildBackgroundImageStylesByViewport(this.props);
            this.currentStyle = this._getCurrentStyle();

            if (this.currentStyle) {
                const backgroundImageClassName = isAuthoringEditEnabled(this.props.request)
                    ? `${BACKGROUND_IMAGE_CLASS_NAME} ms-editable-field enabled`
                    : BACKGROUND_IMAGE_CLASS_NAME;
                const processedAtributes = this._processAttributes(
                    backgroundImageClassName,
                    this.currentStyle,
                    this.stylesByViewport,
                    this._isLazyload()
                );
                const attributes = this._getAttributes(processedAtributes);
                const eventHandlers = isAuthoringEditEnabled(this.props.request) ? { onDoubleClick: this._onChangeHandler } : {};

                if (this.state.updateAttributes) {
                    // Update component with the latest attributes.
                    return (
                        <div {...eventHandlers} {...attributes}>
                            {children}
                        </div>
                    );
                } else if (this._isLazyload()) {
                    // On lazyload, only include the class name on the first render.
                    return (
                        <div {...eventHandlers} className={backgroundImageClassName}>
                            {children}
                        </div>
                    );
                } else {
                    // On lazyload disabled, render the default attributes from server side.
                    return (
                        <div {...eventHandlers} {...this._getDefaultAttributes()}>
                            {children}
                        </div>
                    );
                }
            } else {
                // in no style is found, return the container content.
                return <>{children}</>;
            }
        } catch {
            // in case of any failure, return the container content.
            return <>{children}</>;
        }
    }

    private _processAttributes(
        className: string,
        currentStyle: IBackgroundImageStyle,
        stylesByViewport: IGeneric<IBackgroundImageStyle>,
        lazyLoad: boolean
    ) {
        if (lazyLoad) {
            return getBackgroundImageLazyloadAttributes(className, currentStyle, stylesByViewport);
        } else {
            return getBackgroundImageAttributes(className, currentStyle);
        }
    }

    private _getAttributes(processedAtributes: IBackgroundImageAttributes) {
        return { ...processedAtributes, style: { ...processedAtributes?.style } };
    }

    private _onChangeHandler(event: React.SyntheticEvent | { target: { value: string } }): void {
        if (event.target === (event as React.SyntheticEvent).currentTarget) {
            const binding: any = this.props.image;
            const eventTarget: any = event.target;
            binding.$type = eventTarget.value;
        }
    }

    private _componentResize(): void {
        if (msdyn365Commerce.isBrowser) {
            const selectedStyle = this._getCurrentStyle();

            if (this.mounted || (selectedStyle && !_isEqual(this.currentStyle, selectedStyle))) {
                this.setState({ updateAttributes: true });
            }
        }
    }

    private _getCurrentStyle(): IBackgroundImageStyle | undefined {
        if (msdyn365Commerce.isBrowser) {
            const winInnerWidth: number = window.innerWidth;
            let selectedStyle: IBackgroundImageStyle | undefined;
            Object.keys(this.stylesByViewport).map(vp => {
                if (
                    (this.stylesByViewport[vp]?.minWidth || this.stylesByViewport[vp]?.maxWidth) &&
                    (this.stylesByViewport[vp]?.minWidth || 0) <= winInnerWidth &&
                    winInnerWidth <= (this.stylesByViewport[vp]?.maxWidth || Number.MAX_SAFE_INTEGER)
                ) {
                    selectedStyle = this.stylesByViewport[vp];
                }
            });
            return selectedStyle;
        } else {
            return this._getDefaultStyle();
        }
    }

    private _isLazyload(): boolean {
        // Lazyload by default if nothing is specified
        return !this.props.image?.imageSettings?.disableLazyLoad;
    }

    private _getDefaultStyle(): IBackgroundImageStyle | undefined {
        const { request } = this.props;
        const largestGridSettingViewportName = getLargestGridSettingViewportName(request.gridSettings ?? {});
        return this.stylesByViewport[largestGridSettingViewportName];
    }

    private _getDefaultAttributes(): IBackgroundImageAttributes {
        const style = this._getDefaultStyle();
        const processedAtributes = style
            ? this._processAttributes(BACKGROUND_IMAGE_CLASS_NAME, style, this.stylesByViewport, this._isLazyload())
            : {};
        return this._getAttributes(processedAtributes);
    }
}
